/* Copyright (c) Librestream Technologies. All rights reserved. */
/* eslint-disable */ 

import dateFormat, { masks } from 'dateformat';
import piexif from 'piexifjs';
import * as pako from 'pako';
import {utf8ToString, stringToUtf8, numberToRational} from 'SERVICES/lsexif/exifutils';
import logger from 'SERVICES/lsexif/logger';
import Telestration, {SIZEOF_UINT32, SIZEOF_DOUBLE} from 'SERVICES/lsexif/telestration';

const crypto = require('crypto');

const LSEXIF_LOG_TAG = 'LsExif';
const LSEXIF_LOG_EN = false;

var LSEXIF_CONSTANTS = {
    LSEXIF_MAKERNOTE_KEY: 'LibreStreamMakerNote',
    LSEXIF_MAKERNOTE_VERSION: 1,
};

///////////////////////////////////////////////////////////////////////////////

export default function LsExif() {
    this.r4Tele = new Telestration();
    this.TeleV3 = null;
    this.teleV3Obj = null;
    this.version = LSEXIF_CONSTANTS.LSEXIF_MAKERNOTE_VERSION;
    this.onLoadCallback = null;
    this.onErrorCallback = null;
    this.loaded = false;
    this.metadata = null;
    this.metadataSetList = null;
    this.keywords = null;
    this.dateTime = Date.now();
    this.author = '';
    this.software = '';
    this.title = '';
    this.make = '';
    this.model = '';
    this.comment = '';
    this.gpsLongitude = null;
    this.gpsLongitudeRef = '';
    this.gpsLatitude = null;
    this.gpsLatitudeRef = '';
    this.gpsAltitude = null;
    this.gpsAltitudeRef = null;
};

LsExif.prototype.load = function (_img, _colourCallback) {
    if (_img != null) {
        var self = this;
        LsExif._getImageData(_img, function (_binImage) {
            var blob = new Blob([_binImage], { type: 'image/jpeg' });

             var reader = new FileReader();
             reader.onloadend = function (e) {
                var img = new Image();
                img.onload = function (e) {
                    try {
                        self._parseExif(img.src, _colourCallback);
                        self.loaded = true;
                        if (self.onLoadCallback != null) {
                            self.onLoadCallback();
                        }
                    } catch (e) {
                        logger.error(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, 'Exception! ' + e);

                        if (self.onErrorCallback != null) {
                            self.onErrorCallback();
                        }
                    }
                };
                img.onerror = function (e) {
                    logger.error(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, 'Image load error!');
                    logger.error(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, _img);
                    if (self.onErrorCallback != null) {
                        self.onErrorCallback();
                    }
                };
                img.src = reader.result;
            };
            reader.readAsDataURL(blob);
        });
    }
};

LsExif.prototype.save = function (_img, _telecanvas, _callback) {
    if (_img != null) {
        var self = this;
        LsExif._getImageData(_img, function (_binImage) {
            var blob = new Blob([_binImage], { type: 'image/jpeg' });

            var reader = new FileReader();
            reader.onloadend = function (e) {
                var img = new Image();
                img.onload = function (e) {
                    self._writeExif(img, _telecanvas, _callback);
                };
                img.onerror = function (e) {
                    logger.error(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, 'Image load error!');
                    logger.error(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, _img);
                };
                img.src = reader.result;
            };
            reader.readAsDataURL(blob);
        });
    }
};

LsExif.prototype.saveFromDataUrl = function (_img, _telecanvas, _callback) {
    logger.debug('saveFromDataUrl img data:', _img);
    return new Promise((resolve, reject) => {
        var self = this;
        var reader = new FileReader();
        reader.onloadend = function (e) {
            var img = new Image();
            img.onload = function (e) {
                const exifImage = self._writeExif(img, _telecanvas, _callback);
                resolve(exifImage);
            };
            img.onerror = function (e) {
                logger.error(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, 'Image load error!');
                logger.error(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, _img);
                reject(new Error('Image load error'));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(_img);
    });
};

LsExif._base64ToArrayBuffer = function (base64, contentType) {
    contentType = contentType || base64.match(/^data\:([^\;]+)\;base64,/im)[1] || ''; // e.g. 'data:image/jpeg;base64,...' => 'image/jpeg'
    base64 = base64.replace(/^data\:([^\;]+)\;base64,/gim, '');
    var binary = atob(base64);
    var len = binary.length;
    var buffer = new ArrayBuffer(len);
    var view = new Uint8Array(buffer);
    for (var i = 0; i < len; i++) {
        view[i] = binary.charCodeAt(i);
    }
    return buffer;
};

LsExif._convertDataURItoBlob = function (dataURI) {
    // convert base64/URLEncoded data component to raw binary data held in a string
    var byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
    else byteString = unescape(dataURI.split(',')[1]);

    // separate out the mime component
    var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to a typed array
    var ia = new Uint8Array(byteString.length);
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
};

LsExif._getImageDataFromSrc = function (_src, _callback) {
    if (_src) {
        if (/^data\:/i.test(_src)) {
            // Data URI
            var arrayBuffer = LsExif._base64ToArrayBuffer(_src);
            _callback(arrayBuffer);
        } else if (/^blob\:/i.test(_src)) {
            // Object URL
            var fileReader = new FileReader();
            fileReader.onload = function (e) {
                _callback(e.target.result);
            };
            objectURLToBlob(_src, function (blob) {
                fileReader.readAsArrayBuffer(blob);
            });
        } else {
            var http = new XMLHttpRequest();
            http.onload = function () {
                if (this.status == 200 || this.status === 0) {
                    _callback(http.response);
                } else {
                    throw 'Could not load image';
                }
                http = null;
            };

            http.open('GET', _src, true);
            http.responseType = 'arraybuffer';
            http.send(null);
        }
    }
};

LsExif._getImageData = function (_img, _callback) {
    if (_img.src) {
        this._getImageDataFromSrc(_img.src, _callback);
    } else if (window.FileReader && (_img instanceof window.Blob || _img instanceof window.File)) {
        var fileReader = new FileReader();
        fileReader.onload = function (e) {
            _callback(e.target.result);
        };

        fileReader.readAsArrayBuffer(_img);
    }
};

LsExif.prototype._parseMakerNote = function (makerNote, _colourCallback) {
    if (makerNote != null) {
        var makerNoteKey = utf8ToString(makerNote, 0, LSEXIF_CONSTANTS.LSEXIF_MAKERNOTE_KEY.length);
        logger.debug(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, 'Maker note key: ' + makerNoteKey);
        if (makerNoteKey == LSEXIF_CONSTANTS.LSEXIF_MAKERNOTE_KEY) {
            var i = makerNoteKey.length;
            var dv = new DataView(makerNote.buffer);

            this.version = dv.getUint32(i, true);
            i += SIZEOF_UINT32;

            if (this.version == LSEXIF_CONSTANTS.LSEXIF_MAKERNOTE_VERSION) {
                // get the GUID
                var len = dv.getUint32(i, true);
                i += SIZEOF_UINT32;
                if (len > 0) {
                    this.guid = new Uint8Array(makerNote.buffer, i, len);
                    i += len;
                }

                // get the UTC date time
                var len = dv.getUint32(i, true);
                i += SIZEOF_UINT32;
                if (len > 0) {
                    this.date = utf8ToString(makerNote, i, len);
                    i += len;
                }

                // get the R3 tele blob
                // this is obsolete and can be ignored, we'll record it just in case
                var len = dv.getUint32(i, true);
                i += SIZEOF_UINT32;
                if (len > 0) {
                    this.r3Tele = new Uint8Array(makerNote.buffer, i, len);
                    i += len;
                }

                // get the R4 tele blob
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        this.r4Tele = Telestration.parseTeleBlobR4(makerNote.buffer, i, len, _colourCallback);
                        i += len;
                    }
                }

                // get the watermark flag
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        this.watermark = new Uint8Array(makerNote.buffer, i, len);
                        i += len;
                    }
                }

                // get the keywords
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        this.keywords = utf8ToString(makerNote, i, len);
                        i += len;
                    }
                }

                // get the call GUID
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        this.callGuid = new Uint8Array(makerNote.buffer, i, len);
                        i += len;
                    }
                }

                // get the distance if it exists
                if (i < makerNote.byteLength - 1) {
                    this.distanceAccuracy = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                }
                if (i < makerNote.byteLength - 1) {
                    this.distance = new Uint8Array(makerNote.buffer, i, SIZEOF_DOUBLE);
                    i += SIZEOF_DOUBLE;
                }

                // get the macro mode flag if it exists
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        this.macroMode = dv.getUint8(i, true) != 0;
                        i += len;
                    }
                }

                // get the zoom level if it exists
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        this.zoomLevel = new Uint8Array(makerNote.buffer, i, len);
                        i += len;
                    }
                }

                // get the laser offset if it exists
                if (i < makerNote.byteLength - 1) {
                    this.laserOffsetX = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                }
                if (i < makerNote.byteLength - 1) {
                    this.laserOffsetY = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                }

                // get the device name if it exists
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        this.device = utf8ToString(makerNote, i, len);
                        i += len;
                    }
                }

                // get metadata if it exists
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        this.metadata = utf8ToString(makerNote, i, len);
                        this.metadataSetList = JSON.parse(this.metadata);
                        i += len;
                    }
                }

                // look for V3 telestration blob if it exists
                if (i < makerNote.byteLength - 1) {
                    var len = dv.getUint32(i, true);
                    var v3 = null;
                    i += SIZEOF_UINT32;
                    if (len > 0) {
                        // Use prototype instead of markerNote.slice - this is required for the Java
                        // usage of this function. This does not affect the frontend functionality.
                        var zippedImage = Array.prototype.slice.call(makerNote, i, i + len);

                        // decompress into result
                        try {
                            var result = pako.inflate(zippedImage);
                        } catch (err) {
                            logger.error(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, 'Exception! ' + err);
                            throw err;
                        }
                        var v3 = utf8ToString(result, 0, result.length);
                        i += len;
                    }

                    if (v3 != null) {
                        this.TeleV3 = Telestration.parseTeleBlobR3(v3);
                    }
                }

                // verify the checksum
                var checksumVerified = false;
                if (i <= makerNote.byteLength - 1) {
                    var checksum = 0;
                    for (var j = 0; j < makerNote.byteLength - 1; j++) {
                        checksum ^= dv.getUint8(j, true);
                    }

                    var makerNoteChecksum = dv.getUint8(makerNote.byteLength - 1, true);
                    checksumVerified = checksum == makerNoteChecksum;
                }
                if (!checksumVerified) {
                    throw 'MakerNote checksum incorrect';
                }
            }
        }
    }
    logger.debug(LSEXIF_LOG_EN, LSEXIF_LOG_TAG, this);
};

LsExif.prototype._parseExif = function (_imgSrc, _colourCallback) {
    var allExif = piexif.load(_imgSrc);
    var makerNote = allExif.Exif[piexif.ExifIFD.MakerNote];
    var buffer = [];

    if (allExif["0th"][piexif.ImageIFD.DateTime] != null) {
        const dateTimeParts = allExif["0th"][piexif.ImageIFD.DateTime].split(' ');
        const dateParts = dateTimeParts[0].split(':');
        const timeParts = dateTimeParts[1].split(':');
        this.dateTime = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]), 
            parseInt(timeParts[0]), parseInt(timeParts[1]), parseInt(timeParts[2]));
    }    
    this.author = allExif["0th"][piexif.ImageIFD.Artist];
    this.software = allExif["0th"][piexif.ImageIFD.Software];
    this.title = allExif["0th"][piexif.ImageIFD.ImageDescription];
    this.make = allExif["0th"][piexif.ImageIFD.Make];
    this.model = allExif["0th"][piexif.ImageIFD.Model];
    this.comment = allExif.Exif[piexif.ExifIFD.UserComment]
    // TODO lat/long/altitude

    if (makerNote != null) {
        for (var i = 0; i < makerNote.length; i++) {
            buffer.push(makerNote.charCodeAt(i));
        }

        this._parseMakerNote(new Uint8Array(buffer), _colourCallback);
    }
};

// Used by server-side JavascriptInvoker.java. Returns the appropriate telestration
// sets after invoking _parseExif().
LsExif.prototype._getTelestrationSets = function (_imgSrc) {
    this._parseExif(_imgSrc, null);

    if (this.TeleV3 != null) {
        return this.TeleV3;
    } else if (this.r4Tele != null) {
        return this.r4Tele;
    } else {
        return null;
    }
};

LsExif.prototype._calcMakerNoteLength = function () {
    var length = LSEXIF_CONSTANTS.LSEXIF_MAKERNOTE_KEY.length;

    // version
    if (this.version != null) {
        length += SIZEOF_UINT32;
    }

    // guid
    length += SIZEOF_UINT32;
    if (this.guid != null) {
        length += this.guid.byteLength;
    }

    // date
    length += SIZEOF_UINT32;
    if (this.date != null) {
        length += this.date.length;
    }

    // r3blob
    length += SIZEOF_UINT32;
    if (this.r3Tele != null) {
        length += this.r3Tele.byteLength;
    }

    // r4blob
    length += SIZEOF_UINT32; // length of blob
    length += (this.r4Tele ? this.r4Tele.setTeleBlobR4(null) : 0);

    // watermark
    length += SIZEOF_UINT32;
    if (this.watermark != null) {
        length += this.watermark.byteLength;
    }

    // keywords
    length += SIZEOF_UINT32;
    if (this.keywords != null) {
        length += this.keywords.length;
    }

    // call guid
    length += SIZEOF_UINT32;
    if (this.callGuid != null) {
        length += this.callGuid.byteLength;
    }

    // distanceAccuracy and distance
    length += SIZEOF_UINT32 + (this.distance != null ? this.distance.byteLength : SIZEOF_DOUBLE);

    // macromode
    length += SIZEOF_UINT32;
    if (this.macroMode != null) {
        length += 1;
    }

    // zoom level
    length += SIZEOF_UINT32;
    if (this.zoomLevel != null) {
        length += this.zoomLevel.byteLength;
    }

    // laser offset x & y
    length += SIZEOF_UINT32 * 2;

    // device name
    length += SIZEOF_UINT32;
    if (this.device != null) {
        var device = new Uint8Array(stringToUtf8(this.device));
        length += device.byteLength;
    }

    // metadata
    length += SIZEOF_UINT32;
    if (this.metadata != null) {
        var metadata = new Uint8Array(stringToUtf8(this.metadata));
        length += metadata.length;
    }

    // V3 telestration blob
    length += SIZEOF_UINT32;
    var xx = this.TeleV3 == null ? this.r4Tele.sets : this.TeleV3.sets;
    var obj = xx.map(function (item) {
        item.il = item.items;
        var colour = typeof item.clr === 'string' ? Telestration.hexToArgb(item.strokeStyle) : item.clr;
        for (var i = 0; i < item.items.length; i++) {
            delete item.items[i].c;
            var tn = item.items[i].tn;
            delete item.items[i].tn;

            item.items[i].$type = Telestration.mapValueToTelestrationType(item.items[i].type);
            delete item.items[i].type;

            item.items[i].coords.map(function (xx) {
                xx.x = Math.round(xx.x);
                xx.y = Math.round(xx.y);
            });
            item.items[i].pc = item.items[i].coords;

            item.items[i].tx = item.items[i].text;
            item.items[i].fs = parseInt(item.items[i].fontSize);
            item.items[i].fi = item.items[i].fontId;
            item.items[i].c = colour;
            item.items[i].tn = tn;

            delete item.items[i].coords;
            delete item.items[i].text;
            delete item.items[i].fontSize;
            delete item.items[i].fontId;
        }
        item.clr = colour;
        delete item.items;
        delete item.penDown;
        delete item.currItem;
        delete item.strokeStyle;
        delete item.type;
        return item;
    });

    var jsonObj = JSON.stringify(obj);
    var compressedJson = new Uint8Array(stringToUtf8(jsonObj));
    this.teleV3Obj = pako.gzip(compressedJson);

    length += this.teleV3Obj.byteLength;

    // checksum
    length++;

    return length;
};

LsExif.prototype._writeExif = function (_img, _telecanvas, _callback) {
    var buffer = new Uint8Array(this._calcMakerNoteLength());
    var dv = new DataView(buffer.buffer);
    var i = 0;

    // maker note key
    var makerNoteKey = new Uint8Array(stringToUtf8(LSEXIF_CONSTANTS.LSEXIF_MAKERNOTE_KEY));
    buffer.set(makerNoteKey, i);
    i += makerNoteKey.length;

    // version
    dv.setUint32(i, this.version, true);
    i += SIZEOF_UINT32;

    // guid
    if (this.guid != null) {
        dv.setUint32(i, this.guid.byteLength, true);
        i += SIZEOF_UINT32;
        buffer.set(this.guid, i);
        i += this.guid.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // date
    if (this.date != null) {
        dv.setUint32(i, this.date.length, true);
        i += SIZEOF_UINT32;

        var date = new Uint8Array(stringToUtf8(this.date));
        buffer.set(date, i);
        i += date.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // r3blob
    if (this.r3Tele != null) {
        dv.setUint32(i, this.r3Tele.byteLength, true);
        i += SIZEOF_UINT32;
        buffer.set(this.r3Tele, i);
        i += this.r3Tele.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // r4blob
    if (this.r4Tele != null) {
        var blobsize = this.r4Tele.setTeleBlobR4(null);
        dv.setUint32(i, blobsize, true);
        i += SIZEOF_UINT32;
        i += this.r4Tele.setTeleBlobR4(buffer.buffer, i);    
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }
    
    // watermark
    if (this.watermark != null) {
        dv.setUint32(i, this.watermark.byteLength, true);
        i += SIZEOF_UINT32;
        buffer.set(this.watermark, i);
        i += this.watermark.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // keywords
    if (this.keywords != null) {
        dv.setUint32(i, this.keywords.length, true);
        i += SIZEOF_UINT32;

        var keywords = new Uint8Array(stringToUtf8(this.keywords));
        buffer.set(keywords, i);
        i += keywords.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // callGuid
    if (this.callGuid != null) {
        dv.setUint32(i, this.callGuid.byteLength, true);
        i += SIZEOF_UINT32;
        buffer.set(this.callGuid, i);
        i += this.callGuid.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // distance
    if (this.distanceAccuracy != null) {
        dv.setUint32(i, this.distanceAccuracy, true);
    } else {
        dv.setUint32(i, 0, true);
    }
    i += SIZEOF_UINT32;
    if (this.distance != null) {
        buffer.set(this.distance, i);
        i += this.distance.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        dv.setUint32(i, 0, true);
        i += SIZEOF_DOUBLE;
    }

    // macromode
    if (this.macroMode != null) {
        dv.setUint32(i, 1, true);
        i += SIZEOF_UINT32;
        dv.setUint8(i, this.macroMode);
        i++;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // zoom level
    if (this.zoomLevel != null) {
        dv.setUint32(i, this.zoomLevel.byteLength, true);
        i += SIZEOF_UINT32;
        buffer.set(this.zoomLevel, i);
        i += this.zoomLevel.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // laser offset x
    if (this.laserOffsetX != null) {
        dv.setUint32(i, this.laserOffsetX, true);
    } else {
        dv.setUint32(i, 0, true);
    }
    i += SIZEOF_UINT32;

    // laser offset y
    if (this.laserOffsetY != null) {
        dv.setUint32(i, this.laserOffsetY, true);
    } else {
        dv.setUint32(i, 0, true);
    }
    i += SIZEOF_UINT32;

    // device name
    if (this.device != null) {
        var device = new Uint8Array(stringToUtf8(this.device));
        dv.setUint32(i, device.byteLength, true);
        i += SIZEOF_UINT32;

        buffer.set(device, i);
        i += device.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // metadata
    if (this.metadata != null) {
        var metadata = new Uint8Array(stringToUtf8(this.metadata));
        dv.setUint32(i, metadata.byteLength, true);
        i += SIZEOF_UINT32;
        buffer.set(metadata, i);
        i += metadata.byteLength;
    } else {
        dv.setUint32(i, 0, true);
        i += SIZEOF_UINT32;
    }

    // V3 telestration blob
    var v3Length = this.teleV3Obj == null ? 0 : this.teleV3Obj.byteLength;
    dv.setUint32(i, v3Length, true);
    i += SIZEOF_UINT32;
    buffer.set(this.teleV3Obj, i);
    i += v3Length;

    // calculate the checksum and copy it into the maker note
    var checksum = 0;
    for (var k = 0; k < i; k++) {
        checksum ^= dv.getUint8(k);
    }
    dv.setUint8(i, checksum, true);
    i++;

    // re-write the exif with new makernote
    var exif = piexif.load(_img.src);
    exif.Exif[piexif.ExifIFD.MakerNote] = '';

    for (var j = 0; j < buffer.byteLength; j++) {
        exif.Exif[piexif.ExifIFD.MakerNote] += String.fromCharCode(buffer[j]);
    }

    // re-write other exif attributes
    exif["0th"][piexif.ImageIFD.DateTime] = dateFormat(this.dateTime, "yyyy:mm:dd HH:MM:ss");
    exif["0th"][piexif.ImageIFD.Artist] = this.author ? this.author : '';
    exif["0th"][piexif.ImageIFD.Software] = this.software ? this.software : '';
    exif["0th"][piexif.ImageIFD.ImageDescription] = this.title ? this.title : '';
    exif["0th"][piexif.ImageIFD.Make] = this.make ? this.make : '';
    exif["0th"][piexif.ImageIFD.Model] = this.model ? this.model : '';
    exif.Exif[piexif.ExifIFD.UserComment] = this.comment ? this.comment : '';
    if (this.gpsAltitude !== null && this.gpsLatitude !== null && this.gpsLongitude !== null) {
        exif["GPS"][piexif.GPSIFD.GPSLatitudeRef] = this.gpsLatitude < 0 ? 'S' : 'N';
        exif["GPS"][piexif.GPSIFD.GPSLatitude] = piexif.GPSHelper.degToDmsRational(this.gpsLatitude);
        exif["GPS"][piexif.GPSIFD.GPSLongitudeRef] = this.gpsLatitude < 0 ? 'W' : 'E';
        exif["GPS"][piexif.GPSIFD.GPSLongitude] = piexif.GPSHelper.degToDmsRational(this.gpsLongitude);
        exif["GPS"][piexif.GPSIFD.GPSAltitude] = numberToRational(this.gpsAltitude);
        exif["GPS"][piexif.GPSIFD.GPSAltitudeRef] = this.gpsAltitude < 0 ? 1 : 0;
    } 
    // create the thumbnail
    exif.thumbnail = LsExif._createThumbnail(_img, _telecanvas);

    // return the modified image
    var exifImage = piexif.insert(piexif.dump(exif), _img.src);
    if (_callback != null) {
        _callback(exifImage);
    }
    return exifImage;
};

// Generate a sha1 hash of certain exif tags for efficient image sharing
LsExif.prototype.generateHash = function() {
    var sha1 = crypto.createHash('sha1');
    sha1.update(dateFormat(this.dateTime, "yyyy:mm:dd HH:MM:ss"));
    if (this.software && this.software.length > 0) sha1.update(this.software);
    if (this.author && this.author.length > 0) sha1.update(this.author);
    if (this.title && this.title.length > 0) sha1.update(this.title);
    if (this.comment && this.comment.length > 0) sha1.update(this.comment);
    if (this.gpsLongitude && this.gpsLongitude.length > 0) sha1.update(this.gpsLongitude);
    if (this.gpsLongitudeRef && this.gpsLongitudeRef.length > 0) sha1.update(this.gpsLongitudeRef);
    if (this.gpsLatitude && this.gpsLatitude.length > 0) sha1.update(this.gpsLatitude);
    if (this.gpsLatitudeRef && this.gpsLatitudeRef.length > 0) sha1.update(this.gpsLatitudeRef);
    if (this.gpsAltitude && this.gpsAltitude.length > 0) sha1.update(this.gpsAltitude);
    if (this.gpsAltitudeRef && this.gpsAltitudeRef.length > 0) sha1.update(this.gpsAltitudeRef);
    if (this.guid && this.guid.byteLength > 0) sha1.update(this.guid);
    if (this.r3Tele && this.r3Tele.byteLength > 0) sha1.update(this.r3Tele);
    if (this.r4Tele) {
        var blobsize = this.r4Tele.setTeleBlobR4(null);
        if (blobsize > 0) {
            var buffer = new Uint8Array(blobsize);
            this.r4Tele.setTeleBlobR4(buffer.buffer, 0);
            sha1.update(buffer);
        }
    }
    if (this.watermark && this.watermark.byteLength > 0) sha1.update(this.watermark);
    if (this.keywords && this.keywords.length > 0) sha1.update(this.keywords);
    if (this.callGuid && this.callGuid.byteLength > 0) sha1.update(this.callGuid);
    return sha1.digest();
}

LsExif._createThumbnail = function (_img, _telecanvas) {
    var canvas = document.createElement('canvas');

    canvas.height = 96;
    canvas.width = (canvas.height * _img.naturalWidth) / _img.naturalHeight;

    canvas.getContext('2d').drawImage(_img, 0, 0, canvas.width, canvas.height);
    if (_telecanvas != null) {
        canvas.getContext('2d').drawImage(_telecanvas, 0, 0, canvas.width, canvas.height);
    }

    var dv = new DataView(LsExif._base64ToArrayBuffer(canvas.toDataURL('image/jpeg')));
    var thumbnail = '';
    for (var j = 0; j < dv.byteLength; j++) {
        thumbnail += String.fromCharCode(dv.getUint8(j, true));
    }
    return thumbnail;
};